import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';

export default function RotationList() {
  const [rotations, setRotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    fetchRotations();
  }, []);

  const fetchRotations = async () => {
    try {
      const { data } = await api.get('/api/rotations');
      setRotations(data);
    } catch (err) {
      console.error('[RotationList] Failed to fetch rotations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (rotation) => {
    try {
      const { data } = await api.put(`/api/rotations/${rotation.id}`, {
        is_active: !rotation.is_active,
      });
      setRotations(rotations.map((r) =>
        r.id === rotation.id ? { ...r, is_active: data.is_active } : r
      ));
    } catch (err) {
      console.error('[RotationList] Toggle failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/rotations/${confirmItem.id}`);
      setRotations(rotations.filter((r) => r.id !== confirmItem.id));
    } catch (err) {
      console.error('[RotationList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando rotaciones...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Rotaciones</h1>
        <Link to="/rotations/create" className="btn btn-primary btn-sm">
          + Nueva Rotación
        </Link>
      </div>

      {rotations.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No hay rotaciones. Crea tu primera rotación para empezar.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tracks</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rotations.map((rotation) => (
                <tr key={rotation.id}>
                  <td className="font-medium text-dark-100">
                    <Link to={`/rotations/${rotation.id}`} className="hover:text-primary-400">
                      {rotation.name}
                    </Link>
                  </td>
                  <td className="text-dark-300">{rotation.track_count || 0}</td>
                  <td className="text-sm text-dark-400">{rotation.rotation_type || 'secuencial'}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={rotation.is_active ? 'active' : 'paused'} size="sm" />
                      <button
                        onClick={() => handleToggleActive(rotation)}
                        className="text-xs text-dark-500 hover:text-dark-300"
                        title={rotation.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {rotation.is_active ? '●' : '○'}
                      </button>
                    </div>
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/rotations/${rotation.id}`}
                      className="text-xs text-dark-400 hover:text-dark-200 mr-1"
                      title="Editar"
                    >
                      ✎
                    </Link>
                    <button
                      onClick={() => {
                        setConfirmItem(rotation);
                        setConfirmOpen(true);
                      }}
                      className="text-xs text-error-400 hover:text-error-300"
                      title="Eliminar"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Rotación"
        message={`¿Seguro que quieres eliminar "${confirmItem?.name}"? Esta acción no se puede deshacer.`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
